// useRbac — the authorization read seam every feature `useXRbac()` extends and `permissionGuard`
// (05) consumes (contract 07 §9/§12). Ledger A4: guards read `isSysAdmin` from HERE, never from
// auth's `useSession()`. The client permission mirror is COSMETIC — the server enforces (BE-ACC-01);
// this composable adds NO id allowlist of its own, and there is no longer one to add: every field
// below reflects the accountStore's authoritative `rbac` section, and the client allowlist seam
// this used to lean on (`SYS_ADMIN_IDS`, once in a shared access-config file — now deleted whole)
// is GONE (audit 18613 forbids a client allowlist deciding authority, even as a fallback).
// Cold-boot safe: pre-`ready` the store defaults give `permissions={}`, `isSysAdmin=false`,
// `onlyDashboard=false` → no false redirect (contract §12/§14).
import { computed, type ComputedRef } from 'vue'

import type { PermissionKey, PermissionMap } from '../models/account.types'
import { useAccountStore } from '../stores/accountStore'
import { useWorkspaceStore } from '../stores/workspaceStore'

export interface UseRbac {
  permissions: ComputedRef<PermissionMap>
  isSysAdmin: ComputedRef<boolean>
  /**
   * `rbac.can_access_admin_panel` — the admin-bypass persona (the backend's `is_admin` flag OR the
   * sysadmin identity), which is what its admin-panel gate actually admits. A SUPERSET of
   * `isSysAdmin`, read `=== true` in the store so an absent bit never grants (ORP-21835 item 4).
   */
  canAccessAdminPanel: ComputedRef<boolean>
  onlyDashboard: ComputedRef<boolean>
  /** `rbac.is_account_holder` — the caller owns the selected account (server-computed). */
  isAccountHolder: ComputedRef<boolean>
  /** `rbac.roles` — the caller's role labels on the selected account (server-computed). */
  roles: ComputedRef<string[]>
  /** Truthy read of `accountStore.getPermission(key)` (contract §12). */
  hasPermission: (key: PermissionKey) => boolean
  /** True when ANY of `keys` is permitted. */
  hasAny: (keys: PermissionKey[]) => boolean
  /**
   * May this user write trend keywords in the selected workspace? (contract 25 §21.5, BE-25-08.)
   *
   * **Only an explicit `false` denies.** The backend fills `rbac.capabilities` solely for a
   * bootstrap that carried a `workspace_id`, and `rbac` is otherwise treated as account-scoped —
   * `bootAccountOnly` (which `loadSelectedAccountContext` uses after every trend-keyword write)
   * returns `{}`, and a workspace switch does not refresh the section at all. Defaulting an
   * unanswered read to `false` would therefore make the activation buttons vanish the moment a
   * user successfully added a keyword, and hide them from every legitimate editor mid-session.
   *
   * Erring open is the correct direction here and costs nothing: this mirror is COSMETIC
   * (BE-ACC-01) — both write endpoints sit behind the server's workspace `editor` gate, so a user
   * who should not write gets a coded 403 whether or not the button rendered. Erring closed would
   * break real editors, which the server cannot undo.
   *
   * NOT a quota check. Package limits live in `permissions.trend_keyword_count_` and surface as
   * `403 PACKAGE_FORBIDDEN`; a quota-exhausted editor must still SEE the affordance so the
   * upgrade path is reachable.
   */
  canMutateTrendKeywords: ComputedRef<boolean>
}

export function useRbac(): UseRbac {
  const accountStore = useAccountStore()
  const workspaceStore = useWorkspaceStore()

  const hasPermission = (key: PermissionKey): boolean => Boolean(accountStore.getPermission(key))
  const hasAny = (keys: PermissionKey[]): boolean => keys.some((key) => hasPermission(key))

  return {
    permissions: computed(() => accountStore.permissions),
    isSysAdmin: computed(() => accountStore.isSysAdmin),
    canAccessAdminPanel: computed(() => accountStore.canAccessAdminPanel),
    onlyDashboard: computed(() => workspaceStore.onlyDashboardPermission),
    isAccountHolder: computed(() => accountStore.isAccountHolder),
    roles: computed(() => accountStore.roles),
    hasPermission,
    hasAny,
    canMutateTrendKeywords: computed(
      () => accountStore.capabilities.trend_keywords_write !== false,
    ),
  }
}
